import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { BsFillTelephoneFill } from "react-icons/bs";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaStore } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import { NavLink, Outlet, useLoaderData } from "react-router-dom";
import useStore from "../../hooks/useStore";
import version from "../../version";
import Error from "../Error";
import { Button, Col, Flex, Row, Space, Spin, Typography } from "antd";
// import "../multistore/index.css";

const { Text, Paragraph } = Typography;

export async function loader({ request }) {
  const url = new URL(request.url);
  const storeId =
    url.searchParams.get("store_id") || sessionStorage.getItem("storeId");
  const integratorId =
    url.searchParams.get("pos_integrator_id") ||
    sessionStorage.getItem("integratorId");
  const token =
    url.searchParams.get("token") || sessionStorage.getItem("token");
  const callbackUrl =
    url.searchParams.get("callback_url") ||
    sessionStorage.getItem("callbackUrl");

  if (!storeId || !integratorId || !token) {
    throw new Response("", {
      status: 404,
      statusText: "Page not found.",
    });
  }
  sessionStorage.setItem("token", token);

  return { storeId, integratorId, token, callbackUrl };
}

export default function DeliveryServices() {
  const { store, setStore } = useStore();
  const { storeId, integratorId, token, callbackUrl } = useLoaderData();
  const [errMsg, setErrMsg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscriptionEnded, setIsSubscriptionEnded] = useState(false);
  const subscriptionEndStatus = useMemo(
    () => new Set(["inactive", "canceled"]),
    [],
  );
  console.log(storeId, integratorId, token, callbackUrl, ">>>>>>> CALLBACK");
  useEffect(() => {
    let isMounted = true;
    const getStoreInfo = async () => {
      try {
        const axiosInit = axios.create({
          baseURL: process.env.REACT_APP_BEVZ_API,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const storeDetails = await axiosInit
          .get(
            `/integrator-service/integrators/${integratorId}/stores/${storeId}`,
          )
          .then(({ data }) => data?.data?.[0]);

        if (subscriptionEndStatus.has(storeDetails?.subscriptionStatus)) {
          isMounted && setIsSubscriptionEnded(true);
          return;
        }

        const sensitiveData = await axiosInit
          .get(
            `/integrator-service/integrators/${integratorId}/stores/${storeId}/sensitive-data`,
          )
          .then(({ data }) => data?.data)
          .catch((err) => console.error(err));

        sessionStorage.setItem("storeId", storeId);
        sessionStorage.setItem("integratorId", integratorId);
        sessionStorage.setItem("callbackUrl", callbackUrl);
        const salesTax = sessionStorage.getItem(storeId) || "";
        isMounted && setErrMsg(null);
        isMounted &&
          setStore((prev) => ({
            ...storeDetails,
            integratorId,
            callbackUrl,
            formData: sensitiveData,
            salesTax,
            token,
          }));
      } catch (err) {
        setErrMsg(err?.response?.data?.errors || err.message);
        console.error(err?.response || err);
        console.error(JSON.stringify(err));
      } finally {
        setIsLoading(false);
      }
    };

    getStoreInfo();

    return () => {
      isMounted = false;
    };
  }, [
    integratorId,
    storeId,
    callbackUrl,
    setStore,
    subscriptionEndStatus,
    token,
  ]);

  if (isLoading) return <Spin fullscreen tip="Loading" size="large" />;

  if (errMsg) {
    return <Error errors={errMsg} status={401} />;
  }

  if (isSubscriptionEnded) {
    return (
      <Error
        errors={"Page restricted please update your subscription."}
        status={403}
      />
    );
  }

  return (
    <Row
      style={{
        minHeight: "100vh",
        minWidth: "100vw",
        backgroundColor: "#f1f5f9",
      }}
    >
      <Col xs={{ span: 22, offset: 1 }} xl={{ span: 18, offset: 3 }}>
        <Flex vertical style={{ height: "100%" }}>
          <Store {...store} callbackUrl={callbackUrl} />
          <Navigation />

          <div style={{ flex: 1, backgroundColor: "#F8FAFC" }}>
            <Outlet />
          </div>
          <div
            style={{
              padding: "0.25rem 0",
              textAlign: "center",
              fontStyle: "italic",
              color: "#9CA3AF",
              fontSize: "12px",
            }}
          >
            POS Integrator v{version}
          </div>
        </Flex>
      </Col>
    </Row>
  );
}

function Store({ storeId, name, email, address, phone, callbackUrl }) {
  return (
    <Row
      style={{
        backgroundColor: "#E2E8F0",
        padding: "0.5rem 0.5rem 1rem 0.5rem",
      }}
      justify="space-between"
    >
      <Col>
        <Space size={1}>
          <FaStore color="#9CA3AF" />
          <Text strong style={{ fontSize: "18px" }}>
            {name}
          </Text>
        </Space>
        <br />
        <Space size={1}>
          <MdEmail color="#9CA3AF" /> <Text italic>{email}</Text>
          <BsFillTelephoneFill color="#9CA3AF" /> <Text italic>{phone}</Text>
        </Space>
        <br />
        <Space size={1}>
          <FaMapMarkerAlt color="#9CA3AF" />{" "}
          <Text>
            {address?.street1} {address?.street2} {address?.city}{" "}
            {address?.state} {address?.zipCode}
          </Text>
        </Space>
      </Col>
      <Col>
        {callbackUrl ? (
          <Button
            onClick={() => window.open(callbackUrl, "_self")}
            type="primary"
            danger
          >
            CLOSE
          </Button>
        ) : null}
      </Col>
    </Row>
  );
}

function Navigation() {
  const routes = [
    { to: "doordash", name: "DOORDASH", enable: true },
    { to: "grubhub", name: "GRUBHUB", enable: true },
    { to: "ubereats", name: "UBEREATS", enable: true },
    // { to: "bevz", name: "BEVZ", enable: false },
  ];
  const defaultStyle = {
    width: "100px",
    height: "100%",
    marginRight: "0.5rem",
    fontWeight: "600",
  };
  return (
    <Flex align="center" style={{ backgroundColor: "#00005F", height: "50px" }}>
      {routes.map((route) => (
        <NavLink
          key={route.name}
          to={`/pos/delivery-services/${route.to}`}
          style={({ isActive, isPending }) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...(isActive && route.enable
              ? {
                  ...defaultStyle,
                  backgroundColor: "#1E3A8A",
                  color: "#FBBF24",
                }
              : route.enable === false
              ? { ...defaultStyle, pointerEvents: "none", color: "#9CA3AF" }
              : { ...defaultStyle, color: "white" }),
          })}
        >
          {route.name}
        </NavLink>
      ))}
    </Flex>
  );
}
